/**
 * A QR code, rendered as SVG.
 *
 * `qrcode-generator` does the encoding — Reed-Solomon and mask selection are
 * not things to hand-write, and getting them subtly wrong produces a code that
 * scans into garbage rather than failing visibly. It has no dependencies of its
 * own, which is why it is the one chosen for a page on the auth path.
 *
 * SVG rather than a canvas or an image: it stays crisp at any size, needs no
 * ref or effect to draw, prints correctly, and carries the theme's colours
 * instead of a baked-in black.
 */

import React from 'react';
import qrcode from 'qrcode-generator';

export interface QrCodeProps {
  value: string;
  /** Rendered size in pixels. */
  size?: number;
  /** Accessible description; the value itself is never read out. */
  label?: string;
  className?: string;
}

export const QrCode: React.FC<QrCodeProps> = ({
  value,
  size = 176,
  label = 'QR code',
  className = '',
}) => {
  const path = React.useMemo(() => {
    // Type 0 = pick the smallest version that fits. 'M' correction is the
    // level every authenticator app's docs assume.
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();

    const count = qr.getModuleCount();
    const segments: string[] = [];
    for (let row = 0; row < count; row += 1) {
      for (let column = 0; column < count; column += 1) {
        if (qr.isDark(row, column)) segments.push(`M${column} ${row}h1v1h-1z`);
      }
    }
    return { d: segments.join(''), count };
  }, [value]);

  /*
    The quiet zone is part of the spec, not padding: scanners need four clear
    modules around the symbol, and a QR flush against a card edge often will
    not read at all.
  */
  const quiet = 4;
  const extent = path.count + quiet * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      role="img"
      aria-label={label}
      className={className}
      shapeRendering="crispEdges"
    >
      {/* Always light behind the modules, whatever the theme: a dark-on-dark
          QR is unreadable, and scanners do not invert. */}
      <rect width={extent} height={extent} fill="#ffffff" />
      <g transform={`translate(${quiet} ${quiet})`}>
        <path d={path.d} fill="#000000" />
      </g>
    </svg>
  );
};
